<template>
  <Flex direction="column">
    <Flex
      v-for="transaction in displayedTransactions"
      :key="transaction.id"
      align="center"
      justify="between"
      v-test="'sale-transaction'"
    >
      <Flex align="center">
        <BaseHeading
          v-if="transaction.type"
          size="s"
        >
          {{ $t(`global.payments.${transaction.type.toLowerCase()}`, brandNamePay()) }}
        </BaseHeading>
        <BaseText
          v-if="transaction.prepaidCard?.description || transaction.giftcard?.code"
          inline
        >
          {{ transaction.prepaidCard?.description || transaction.giftcard?.code }}
        </BaseText>
        <BaseText
          v-else-if="cashupEnabled"
          size="s"
        >
          {{ filters.dateTime(transaction.transactionAt || dayjs()) }}
        </BaseText>
      </Flex>
      <BaseText v-test="'sale-transaction-amount'">
        {{ filters.currency(transaction.amount || 0) }}
      </BaseText>
    </Flex>
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { computed } from 'vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { brandNamePay } from '@/helpers/brand-name';
import { useRegisterStore } from '@/modules/register/stores/register';
import { useUserStore } from '@/stores/user';
import type { Transaction as TransactionType } from '@/types';

const { cashupEnabled } = useRegisterStore();
const { hasFeatureFlag } = useUserStore();
const compliantRegister = hasFeatureFlag('compliant-register');

const props = defineProps<{
  transactions: TransactionType[];
}>();

const sortedTransactions = computed(() =>
  [...props.transactions].sort((a, b) =>
    dayjs(a.transactionAt).diff(dayjs(b.transactionAt)),
  ),
);

const groupedTransactions = computed(() => {
  const result: TransactionType[] = [];
  props.transactions.forEach((transaction) => {
    let existing: TransactionType | undefined;
    if (
      transaction.type !== 'GIFTCARD' &&
      transaction.type !== 'PREPAID_CARD' &&
      transaction.type !== 'POS'
    ) {
      existing = result.find((t) => t.type === transaction.type);
    }
    if (existing) {
      existing.amount += transaction.amount;
    } else if (transaction.amount) {
      result.push({ ...transaction });
    }
  });
  return result;
});

const displayedTransactions = computed(() =>
  compliantRegister ? sortedTransactions.value : groupedTransactions.value,
);
</script>
